import React from 'react';
import { MoreHorizontal, CheckCircle } from 'lucide-react';
// import { getCloudinaryUrl } from '../../../utils/imageUtils';

/**
 * Helper to get display image URL using t_800 transformation
 * This handles HEIC and other formats correctly via Cloudinary named transformation
 */
const getDisplayImageUrl = (imageUrl) => {
  if (!imageUrl) return '';
  // Check if it's a Cloudinary URL and use t_800 (which likely includes f_auto and resizing)
  if (imageUrl.includes('cloudinary.com') && imageUrl.includes('/upload/')) {
    return imageUrl.replace('/upload/', '/upload/t_800/');
  }
  return imageUrl;
};

/**
 * ArtworkCard Component
 * Displays a single artwork card with image, overlays, and an Actions button to open the modal
 * Includes clickable check-in toggle when eventDetails is provided
 */
const ArtworkCard = ({
  artwork,
  eventDetails,
  checkInLoading,
  onCheckIn,
  onUnCheckIn,
  onSelectArtwork
}) => {
  const isCheckedIn = eventDetails && artwork.location === eventDetails.gallery_id;
  const canCheckIn = eventDetails && artwork.accepted;

  const handleCheckInToggle = (e) => {
    e.stopPropagation();
    if (!canCheckIn || checkInLoading) return;
    
    if (isCheckedIn) {
      onUnCheckIn && onUnCheckIn(artwork);
    } else {
      onCheckIn && onCheckIn(artwork);
    }
  };

  return (
    <div className="artwork-card-new">
      {/* Image Container - Larger, cleaner */}
      <div className="artwork-image-container" onClick={() => onSelectArtwork && onSelectArtwork(artwork)}>
        <img 
          src={getDisplayImageUrl(artwork.cover_image)} 
          alt={artwork.title} 
          className="artwork-image" 
          onError={(e) => {
            e.target.onerror = null; 
            e.target.src = '/images/placeholder-artwork.jpg';
          }}
        />

        {/* Overlay badges - top left */}
        {artwork.vc_id && (
          <div className="order-number">#{artwork.vc_id}</div>
        )}

        {/* Check-in indicator - top right (clickable if event exists) */}
        {canCheckIn ? (
          <button
            onClick={handleCheckInToggle}
            disabled={checkInLoading}
            className={`checkin-toggle-btn ${isCheckedIn ? 'checked-in' : 'pending'}`}
            title={isCheckedIn ? 'Click to un-check-in' : 'Click to check in'}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              border: '2px solid white',
              backgroundColor: isCheckedIn ? '#16a34a' : '#f97316',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: checkInLoading ? 'wait' : 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              transition: 'all 0.2s ease',
              padding: 0,
              zIndex: 10
            }}
          >
            {isCheckedIn ? (
              <CheckCircle size={18} />
            ) : (
              <span style={{ fontSize: '16px', fontWeight: 'bold' }}>○</span>
            )}
          </button>
        ) : (
          <div className={`status-indicator ${artwork.accepted ? 'accepted' : 'submitted'}`}>
            {artwork.accepted ? '✓' : '○'}
          </div>
        )}

        {/* Price overlay - bottom right */}
        {artwork.price && (
          <div className="price-overlay">
            ${artwork.price.toLocaleString()}
            {artwork.sold && <span className="sold-tag">SOLD</span>}
          </div>
        )}

        {/* Size overlay - bottom left */}
        {artwork.width && artwork.height && (
          <div className="size-overlay">{artwork.width}"×{artwork.height}"</div>
        )}
      </div>

      {/* Compact Info Section */}
      <div className="artwork-card-info">
        <h4 className="artwork-title">{artwork.title}</h4>
        <p className="artwork-artist">
          {artwork.artist.first_name} {artwork.artist.last_name}
        </p>
        {artwork.artist.nickname && (
          <div className="artwork-artist-nickname">
            <span className="nickname-label">public artist name</span>
            <span className="nickname-value">{artwork.artist.nickname}</span>
          </div>
        )}
      </div>

      {/* Simple Actions Button - Opens Modal */}
      <div className="artwork-actions-row">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onSelectArtwork && onSelectArtwork(artwork);
          }}
          className="action-btn actions-btn"
          title="View details and actions"
        >
          <MoreHorizontal size={14} />
          Actions
        </button>
      </div>
    </div>
  );
};

export default ArtworkCard;
